/**
 * Mutation for moving an animal group via the atomic grazing-event endpoint
 * POST /v3/grazingEvents/move.
 *
 * The move is expressed as two explicit operations the backend applies in one
 * transaction: close the currently-open event (move-out date + post cover) and
 * open a new event (move-in date + pre cover) with a client-generated guid that
 * serves as the idempotency key. The endpoint has no NONE-reading side effects.
 *
 * Authorization and cross-farm failures come back as HTTP 409. When the source
 * field has no open event, the caller may send `close.field` to create a
 * move-out-only row instead of closing by guid.
 *
 * Cover values are passed in display units and divided by 10000 here to match
 * the stored-unit convention the reading API uses.
 */
import {
  onlineManager,
  QueryClient,
  QueryKey,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { APP_MUTATION_SCOPE } from "common/mutationScope";
import { invalidateUnlessReplaying } from "common/offlineReplayCoordinator";
import {
  createNetworkMutationRetryDelay,
  shouldRetryNetworkMutation,
} from "common/utils/networkMutationRetry";
import {
  logGrazingOfflineReplay,
  replayErrorPayload,
} from "common/utils/grazingOfflineReplayLog";
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { syncAnimalGroupNowFromMoveEvents } from "model/fieldCacheHelpers";
import type { Field } from "model/fieldTypes";
import { EventType, ManualDMInputResponse } from "model/readingsApiHelpers";
import { deleteAllTilesForField } from "services";
import type { MoveEvent } from "./useAnimalGroupQueries";

export const MOVE_ANIMAL_GROUP_MUTATION_KEY = ["moveAnimalGroup"];

export class GrazingMoveConflictError extends Error {
  constructor(public code: string) {
    super(code);
    this.name = "GrazingMoveConflictError";
  }
}

interface GrazingMovePayload {
  close?: {
    guid: string;
    field?: string | null;
    dateTimeOut: string;
    postEventCover?: number | null;
  } | null;
  open: {
    guid: string;
    field?: string | null;
    movedToShed: boolean;
    dateTimeIn: string;
    preEventCover?: number | null;
    animalGroupGuid?: string | null;
  };
}

async function postGrazingEventMove(
  payload: GrazingMovePayload
): Promise<{ guid: string; idempotent: boolean }> {
  logGrazingOfflineReplay("move.request.start", {
    closeGuid: payload.close?.guid,
    closeField: payload.close?.field,
    closeDateTimeOut: payload.close?.dateTimeOut,
    openGuid: payload.open.guid,
    openField: payload.open.field,
    openDateTimeIn: payload.open.dateTimeIn,
    movedToShed: payload.open.movedToShed,
    animalGroupGuid: payload.open.animalGroupGuid,
  });
  try {
    const res = await fetch(
      `${import.meta.env.VITE_API_ENDPOINT}/v3/grazingEvents/move`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (res.status === 409) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      logGrazingOfflineReplay("move.request.conflict", {
        closeGuid: payload.close?.guid,
        openGuid: payload.open.guid,
        status: res.status,
        backendError: body.error,
      });
      throw new GrazingMoveConflictError(body.error ?? "CONFLICT");
    }
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      logGrazingOfflineReplay("move.request.error-status", {
        closeGuid: payload.close?.guid,
        openGuid: payload.open.guid,
        status: res.status,
        statusText: res.statusText,
        error,
      });
      throw error;
    }
    const result = (await res.json()) as { guid: string; idempotent: boolean };
    logGrazingOfflineReplay("move.request.success", {
      closeGuid: payload.close?.guid,
      openGuid: payload.open.guid,
      responseGuid: result.guid,
      idempotent: result.idempotent,
      status: res.status,
    });
    return result;
  } catch (error) {
    logGrazingOfflineReplay("move.request.error", {
      closeGuid: payload.close?.guid,
      openGuid: payload.open.guid,
      ...replayErrorPayload(error),
    });
    throw error;
  }
}

export interface MoveAnimalGroupVariables {
  animalGroupGuid: string;
  /**
   * Client-generated guid for the new (opened) event. It is the idempotency
   * key, so the caller must generate it once per logical move and reuse it on
   * a resubmit; a replay returns the same event without creating a duplicate.
   */
  openEventGuid: string;
  /**
   * Guid sent as `close.guid`: an existing open event to close, or a
   * client-generated idempotency key for a new move-out-only row (when
   * `closeSourceFieldGuid` is also set).
   */
  closeEventGuid?: string;
  /**
   * When set together with a client-generated `closeEventGuid`, the backend
   * creates a move-out-only row on this field instead of closing by guid.
   */
  closeSourceFieldGuid?: string;
  /** Source field, used for the optimistic clear. Omit for a move from shed. */
  sourceFieldGuid?: string;
  /** Destination field. Omit (with movedToShed) for a move to shed. */
  destFieldGuid?: string;
  movedToShed: boolean;
  moveDate: Date;
  /** Display-unit covers; divided by 10000 before sending. */
  preGrazingCover?: number;
  postGrazingCover?: number;
}

type MoveResult = { guid: string; idempotent: boolean };

interface MoveContext {
  snapshot: Array<[QueryKey, unknown]>;
}

function readingsByFieldKey(fieldGuid: string): QueryKey {
  return queryKeys.events.readings.byField(fieldGuid);
}

function snapshotReadingsForField(
  queryClient: QueryClient,
  fieldGuid?: string
): Array<[QueryKey, unknown]> {
  if (!fieldGuid) return [];
  const key = readingsByFieldKey(fieldGuid);
  return [[key, queryClient.getQueryData<ManualDMInputResponse[]>(key) ?? []]];
}

function upsertReadingForField(
  queryClient: QueryClient,
  fieldGuid: string,
  reading: ManualDMInputResponse
): void {
  queryClient.setQueryData<ManualDMInputResponse[]>(
    readingsByFieldKey(fieldGuid),
    (old = []) => {
      const exists = old.some((item) => item.guid === reading.guid);
      return exists
        ? old.map((item) => (item.guid === reading.guid ? reading : item))
        : [...old, reading];
    }
  );
}

function buildOpenMoveReading(
  vars: MoveAnimalGroupVariables,
  dateIso: string
): ManualDMInputResponse | undefined {
  if (vars.movedToShed || !vars.destFieldGuid) return undefined;
  return {
    guid: vars.openEventGuid,
    manualDryMatterInput: {
      dryMatterValue: vars.preGrazingCover,
      field: vars.destFieldGuid,
      dateTimeMeasurementStart: dateIso,
      dateTimeMeasurementEnd: dateIso,
      event: {
        eventType: EventType.GrazingEvent,
        dateTimeIn: dateIso,
        preEventCover: vars.preGrazingCover,
        animalGroupGuid: vars.animalGroupGuid,
      },
      kind: "user",
      verificationStatus: "Accepted",
    },
  };
}

function buildCloseMoveReading(
  vars: MoveAnimalGroupVariables,
  fieldGuid: string,
  dateIso: string,
  existing?: ManualDMInputResponse
): ManualDMInputResponse | undefined {
  if (!vars.closeEventGuid) return undefined;
  const baseEvent = existing?.manualDryMatterInput.event ?? {
    eventType: EventType.GrazingEvent,
  };
  const dateTimeIn = baseEvent.dateTimeIn ?? dateIso;
  return {
    guid: vars.closeEventGuid,
    manualDryMatterInput: {
      dryMatterValue:
        vars.postGrazingCover ?? existing?.manualDryMatterInput.dryMatterValue,
      field: fieldGuid,
      dateTimeMeasurementStart:
        existing?.manualDryMatterInput.dateTimeMeasurementStart ?? dateTimeIn,
      dateTimeMeasurementEnd: dateIso,
      event: {
        ...baseEvent,
        eventType: EventType.GrazingEvent,
        dateTimeIn,
        dateTimeOut: dateIso,
        postEventCover:
          vars.postGrazingCover ?? baseEvent.postEventCover ?? undefined,
        animalGroupGuid: vars.animalGroupGuid,
      },
      kind: existing?.manualDryMatterInput.kind ?? "user",
      verificationStatus:
        existing?.manualDryMatterInput.verificationStatus ?? "Accepted",
    },
  };
}

function moveMutationOptions(
  queryClient: QueryClient
): UseMutationOptions<
  MoveResult,
  unknown,
  MoveAnimalGroupVariables,
  MoveContext
> {
  return {
    mutationKey: MOVE_ANIMAL_GROUP_MUTATION_KEY,
    networkMode: "online",
    scope: APP_MUTATION_SCOPE,
    retry: shouldRetryNetworkMutation,
    retryDelay: createNetworkMutationRetryDelay(),
    mutationFn: (vars: MoveAnimalGroupVariables): Promise<MoveResult> =>
      postGrazingEventMove({
        close: vars.closeEventGuid
          ? {
              guid: vars.closeEventGuid,
              field: vars.closeSourceFieldGuid ?? null,
              dateTimeOut: new Date(vars.moveDate).toISOString(),
              postEventCover:
                vars.postGrazingCover != null
                  ? vars.postGrazingCover / 10000
                  : null,
            }
          : null,
        open: {
          guid: vars.openEventGuid,
          field: vars.movedToShed ? null : vars.destFieldGuid ?? null,
          movedToShed: vars.movedToShed,
          dateTimeIn: new Date(vars.moveDate).toISOString(),
          preEventCover:
            vars.preGrazingCover != null ? vars.preGrazingCover / 10000 : null,
          animalGroupGuid: vars.animalGroupGuid,
        },
      }),
    onMutate: async (vars: MoveAnimalGroupVariables) => {
      const snapshot: Array<[QueryKey, unknown]> = [];

      const moveEventsKey = queryKeys.animalGroups.moveEvents(
        vars.animalGroupGuid
      );
      await queryClient.cancelQueries({ queryKey: moveEventsKey });
      const previousMoveEvents =
        queryClient.getQueryData<MoveEvent[]>(moveEventsKey) ?? [];
      snapshot.push([moveEventsKey, previousMoveEvents]);
      const dateIso = new Date(vars.moveDate).toISOString();
      const isOutOnlyClose = !!vars.closeSourceFieldGuid;
      let withClosed = previousMoveEvents.map((event) =>
        !isOutOnlyClose && event.guid === vars.closeEventGuid
          ? {
              ...event,
              dateTimeOut: dateIso,
              endDryMatter: vars.postGrazingCover ?? event.endDryMatter ?? null,
            }
          : event
      );
      if (isOutOnlyClose && vars.closeEventGuid && vars.closeSourceFieldGuid) {
        const sourceField = queryClient.getQueryData<Field>(
          queryKeys.fields.detail(vars.closeSourceFieldGuid)
        );
        withClosed = [
          ...withClosed,
          {
            guid: vars.closeEventGuid,
            moveNumber: withClosed.length + 1,
            fieldGuid: vars.closeSourceFieldGuid,
            fieldName: sourceField?.name ?? null,
            dateTimeIn: null,
            dateTimeOut: dateIso,
            startDryMatter: null,
            endDryMatter: vars.postGrazingCover ?? null,
            movedToShed: false,
            kind: "user" as const,
            verificationStatus: "Accepted" as const,
            animalGroupGuid: vars.animalGroupGuid,
          },
        ];
      }
      const destField = vars.destFieldGuid
        ? queryClient.getQueryData<Field>(
            queryKeys.fields.detail(vars.destFieldGuid)
          )
        : undefined;
      const newEvent: MoveEvent = {
        guid: vars.openEventGuid,
        moveNumber: withClosed.length + 1,
        fieldGuid: vars.movedToShed ? null : vars.destFieldGuid ?? null,
        fieldName: vars.movedToShed ? null : destField?.name ?? null,
        dateTimeIn: dateIso,
        dateTimeOut: null,
        startDryMatter: vars.preGrazingCover ?? null,
        endDryMatter: null,
        movedToShed: vars.movedToShed,
        kind: "user",
        verificationStatus: "Accepted",
        animalGroupGuid: vars.animalGroupGuid,
      };
      const updatedMoveEvents = [...withClosed, newEvent];
      queryClient.setQueryData<MoveEvent[]>(moveEventsKey, updatedMoveEvents);

      const affectedReadingFields = [
        vars.sourceFieldGuid,
        vars.destFieldGuid,
        vars.closeSourceFieldGuid,
      ].filter((guid): guid is string => !!guid);
      for (const fieldGuid of new Set(affectedReadingFields)) {
        await queryClient.cancelQueries({
          queryKey: readingsByFieldKey(fieldGuid),
        });
        snapshot.push(...snapshotReadingsForField(queryClient, fieldGuid));
      }

      if (vars.sourceFieldGuid || vars.closeSourceFieldGuid) {
        const closeFieldGuid =
          vars.closeSourceFieldGuid ?? vars.sourceFieldGuid;
        const sourceReadings = closeFieldGuid
          ? queryClient.getQueryData<ManualDMInputResponse[]>(
              readingsByFieldKey(closeFieldGuid)
            ) ?? []
          : [];
        const existingCloseReading = sourceReadings.find(
          (reading) => reading.guid === vars.closeEventGuid
        );
        if (closeFieldGuid) {
          const closeReading = buildCloseMoveReading(
            vars,
            closeFieldGuid,
            dateIso,
            existingCloseReading
          );
          if (closeReading) {
            upsertReadingForField(queryClient, closeFieldGuid, closeReading);
          }
        }
      }

      const openReading = buildOpenMoveReading(vars, dateIso);
      if (openReading && vars.destFieldGuid) {
        upsertReadingForField(queryClient, vars.destFieldGuid, openReading);
      }

      await queryClient.cancelQueries({ queryKey: queryKeys.fields.all });
      snapshot.push(
        ...syncAnimalGroupNowFromMoveEvents(
          queryClient,
          vars.animalGroupGuid,
          updatedMoveEvents,
          [
            vars.sourceFieldGuid,
            vars.destFieldGuid,
            vars.closeSourceFieldGuid,
          ].filter((guid): guid is string => !!guid)
        )
      );

      return { snapshot };
    },
    onError: (
      _error: unknown,
      vars: MoveAnimalGroupVariables,
      context: MoveContext | undefined
    ) => {
      if (context?.snapshot) {
        context.snapshot.forEach(([key, previous]) => {
          queryClient.setQueryData(key, previous);
        });
      } else {
        void invalidateUnlessReplaying(queryClient, {
          queryKey: queryKeys.fields.all,
        });
        void invalidateUnlessReplaying(queryClient, {
          queryKey: queryKeys.animalGroups.moveEvents(vars.animalGroupGuid),
        });
      }
    },
    onSuccess: async (_data: MoveResult, vars: MoveAnimalGroupVariables) => {
      const fieldGuids = [vars.sourceFieldGuid, vars.destFieldGuid].filter(
        (g): g is string => !!g
      );
      await Promise.all(
        fieldGuids.map((fieldGuid) => deleteAllTilesForField(fieldGuid))
      );
    },
    onSettled: async (
      _data: MoveResult | undefined,
      _error: unknown,
      vars: MoveAnimalGroupVariables
    ) => {
      const fieldGuids = [vars.sourceFieldGuid, vars.destFieldGuid].filter(
        (g): g is string => !!g
      );
      await Promise.all(
        fieldGuids.flatMap((guid) => [
          invalidateUnlessReplaying(queryClient, {
            queryKey: queryKeys.fields.detail(guid),
          }),
          invalidateUnlessReplaying(queryClient, {
            queryKey: queryKeys.events.readings.byField(guid),
          }),
        ])
      );
      await invalidateUnlessReplaying(queryClient, {
        queryKey: queryKeys.fields.all,
      });
      await invalidateUnlessReplaying(queryClient, {
        queryKey: queryKeys.animalGroups.moveEvents(vars.animalGroupGuid),
      });
    },
  };
}

export function registerMoveAnimalGroupMutationDefaults(
  queryClient: QueryClient
): void {
  queryClient.setMutationDefaults(
    MOVE_ANIMAL_GROUP_MUTATION_KEY,
    moveMutationOptions(queryClient)
  );
}

function useQueuedMoveMutation(
  options: UseMutationOptions<
    MoveResult,
    unknown,
    MoveAnimalGroupVariables,
    MoveContext
  >
): UseMutationResult<
  MoveResult,
  unknown,
  MoveAnimalGroupVariables,
  MoveContext
> {
  const mutation = useMutation(options);

  const mutateAsync = ((
    variables: MoveAnimalGroupVariables,
    mutateOptions?: Parameters<typeof mutation.mutateAsync>[1]
  ) => {
    if (!onlineManager.isOnline()) {
      mutation.mutate(variables, mutateOptions);
      return Promise.resolve({
        guid: variables.openEventGuid,
        idempotent: false,
      });
    }
    return mutation.mutateAsync(variables, mutateOptions);
  }) as typeof mutation.mutateAsync;

  return { ...mutation, mutateAsync };
}

export function useMoveAnimalGroup() {
  const queryClient = useQueryClient();
  return useQueuedMoveMutation(moveMutationOptions(queryClient));
}
